'use client';

/**
 * Everything the chat page KNOWS, separated from everything it RENDERS.
 *
 * Contexts, the two TanStack queries, the start-screen seeds handed to the
 * first message of a new chat, chat CRUD, the lifecycle effects, and the tab
 * controller. `ChatPage` itself is then just the layout that consumes this.
 */

import { useCallback, useState } from 'react';
import { useGlobalModalStack } from '@west-tennessee-consulting/ui/components/global-modal-stack';
import { useGlobalDrawerStack } from '@west-tennessee-consulting/ui/components/global-drawer-stack';
import { useChatId } from '../../contexts/ChatIdContext.js';
import { useChatData } from '../../contexts/ChatDataContext.js';
import { useMeridianEntityContextOptional } from '../../contexts/EntityContextProvider.js';
import { useBusinessInfo } from '../../hooks/useBusinessInfo.js';
import { useChatsQuery, useMessagesQuery } from '../../hooks/api/index.js';
import { useChatPageHandlers } from './useChatPageHandlers.js';
import { useChatPageEffects } from './useChatPageEffects.js';
import { useChatPageTabs } from './useChatPageTabs.js';
import type { FilePart } from '../../lib/db/types.js';
import type { AttachedSkill } from '../skills/types.js';

export interface UseChatPageControllerParams {
  apiBaseUrl: string;
  meridianFetchEnabled: boolean;
  /** Already defaulted by ChatPage, so never `undefined` here. */
  launchRequest: { query: string; nonce: number } | null;
}

export function useChatPageController({
  apiBaseUrl,
  meridianFetchEnabled,
  launchRequest,
}: UseChatPageControllerParams) {
  const { chatId: urlChatId, setChatId: setUrlChatId } = useChatId();
  const { businessInfo: userContext } = useBusinessInfo({ apiBaseUrl });
  const entityContext = useMeridianEntityContextOptional();
  // Auth token for meridian API (POST /api/meridian/messages, etc.) is set by
  // the host app (e.g. hub ClientProviders) via setAuthTokenGetter() with a
  // sync Supabase token getter. Do not overwrite it here so the hub's token is
  // always used.

  const {
    selectedChatId,
    selectedChat,
    isLoading,
    error,
    setSelectedChatId,
    setSelectedChat,
    setIsLoading,
    setError,
  } = useChatData();

  // UI-only local state.
  const [showShareModal, setShowShareModal] = useState(false);
  const [initialQuery, setInitialQuery] = useState<string>('');
  // Context captured on the start screen and handed to the first message of the
  // chat it creates: attachments already uploaded under the reserved chat id,
  // and any skill the user attached before sending.
  const [initialFiles, setInitialFiles] = useState<FilePart[]>([]);
  const [initialSkill, setInitialSkill] = useState<AttachedSkill | null>(null);

  const { openModal, closeModal } = useGlobalModalStack();
  const { openDrawer, closeDrawer, isDrawerOpen } = useGlobalDrawerStack();
  const isSidebarOpen = isDrawerOpen('mobile-sidebar-drawer');

  const chatsQuery = useChatsQuery({
    apiBaseUrl,
    status: 'all',
    limit: 10,
    entityType: entityContext?.entityType,
    entityId: entityContext?.entityId,
    enabled: meridianFetchEnabled,
  });

  const messagesQuery = useMessagesQuery({
    apiBaseUrl,
    chatId: selectedChatId || '',
    limit: 10,
    enabled: !!selectedChatId,
  });

  // Chat CRUD + sidebar handlers.
  const handlers = useChatPageHandlers({
    apiBaseUrl,
    entityContext: entityContext || undefined,
    chatsQuery,
    selectedChatId,
    selectedChat,
    setUrlChatId,
    setSelectedChat,
    setInitialQuery,
    setInitialFiles,
    setInitialSkill,
    setIsLoading,
    setError,
    isSidebarOpen,
    openDrawer,
    closeDrawer,
  });

  // Lifecycle effects: URL/selection sync, fetched-chat sync, chat-switch
  // cleanup, and one-shot launch (omnibox/deep-link).
  useChatPageEffects({
    urlChatId,
    selectedChatId,
    messagesChat: messagesQuery.chat,
    launchRequest,
    setSelectedChatId,
    setSelectedChat,
    setUrlChatId,
    setInitialQuery,
    setInitialFiles,
    setInitialSkill,
    setError,
    setShowShareModal,
    closeModal,
    closeDrawer,
    handleStartChatWithQuery: handlers.handleStartChatWithQuery,
  });

  const tabs = useChatPageTabs({
    selectedChatId,
    setUrlChatId,
    handleDeleteChat: handlers.handleDeleteChat,
    handleArchiveChat: handlers.handleArchiveChat,
  });

  const clearInitialQuery = useCallback(() => setInitialQuery(''), []);

  const handleBackToList = useCallback(() => {
    setSelectedChatId(null);
    setSelectedChat(null);
    setUrlChatId(null);
  }, [setSelectedChatId, setSelectedChat, setUrlChatId]);

  /** Single-pane "back": the URL is owned by the caller's own navigation. */
  const handleBackToLoadingList = useCallback(() => {
    setSelectedChatId(null);
    setSelectedChat(null);
  }, [setSelectedChatId, setSelectedChat]);

  const handleShareChatFromPane = useCallback(
    (chatId: string) => {
      // The share modal is bound to the selected chat, so sharing from a
      // background pane selects it first rather than sharing the wrong chat.
      if (chatId !== selectedChatId) setUrlChatId(chatId);
      setShowShareModal(true);
    },
    [selectedChatId, setUrlChatId]
  );

  const openShareModal = useCallback(() => setShowShareModal(true), []);
  const closeShareModal = useCallback(() => setShowShareModal(false), []);
  const dismissError = useCallback(() => setError(null), [setError]);

  return {
    selectedChatId,
    selectedChat,
    isLoading,
    error,
    userContext,
    isSidebarOpen,
    setUrlChatId,
    chatsQuery,
    messagesQuery,
    initialQuery,
    initialFiles,
    initialSkill,
    showShareModal,
    openShareModal,
    closeShareModal,
    clearInitialQuery,
    dismissError,
    handleBackToList,
    handleBackToLoadingList,
    handleShareChatFromPane,
    openModal,
    ...handlers,
    ...tabs,
  };
}
